import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import ContactForm from "./ContactForm";
import useDesktop from "../hooks/useDesktop";

const TYPING_SPEED = 20;
const LINE_PAUSE = 140;

function getStoredDone(storageKey) {
  if (!storageKey || typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

export default function TerminalDetailOverlay({
  isOpen,
  onClose,
  title = "Detail",
  subtitle = "Terminal Details",
  lines = [],
  storageKey,
  ctaLabel,
  ctaPath,
  ctaMode = "navigate",
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}) {
  const navigate = useNavigate();
  const isDesktop = useDesktop();
  const shouldSkipTyping = useMemo(
    () => !isDesktop || getStoredDone(storageKey),
    [isDesktop, storageKey]
  );
  const [visibleLines, setVisibleLines] = useState(() =>
    shouldSkipTyping ? lines.map((line) => ({ ...line, displayText: line.text })) : []
  );
  const [lineIndex, setLineIndex] = useState(() => (shouldSkipTyping ? lines.length : 0));
  const [charIndex, setCharIndex] = useState(0);
  const [done, setDone] = useState(() => shouldSkipTyping);
  const [showInlineContact, setShowInlineContact] = useState(false);
  const scrollAreaRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    if (shouldSkipTyping) {
      setVisibleLines(lines.map((line) => ({ ...line, displayText: line.text })));
      setLineIndex(lines.length);
      setCharIndex(0);
      setDone(true);
      setShowInlineContact(false);
      return;
    }
    setVisibleLines([]);
    setLineIndex(0);
    setCharIndex(0);
    setDone(false);
    setShowInlineContact(false);
  }, [isOpen, lines, shouldSkipTyping]);

  useEffect(() => {
    if (!isOpen || done) return;
    if (lineIndex >= lines.length) {
      setDone(true);
      return;
    }

    const line = lines[lineIndex];

    if (charIndex === 0) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: "" }]);
        setCharIndex(1);
      }, LINE_PAUSE);
      return () => clearTimeout(timer);
    }

    if (charIndex <= line.text.length) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            ...line,
            displayText: line.text.slice(0, charIndex),
          };
          return next;
        });
        setCharIndex((c) => c + 1);
      }, TYPING_SPEED);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setLineIndex((idx) => idx + 1);
      setCharIndex(0);
    }, LINE_PAUSE);
    return () => clearTimeout(timer);
  }, [charIndex, done, isOpen, lineIndex, lines]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isTypingField =
        activeTag === "input" ||
        activeTag === "textarea" ||
        activeTag === "select" ||
        document.activeElement?.isContentEditable;
      if (isTypingField) return;

      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft" && hasPrev) {
        e.preventDefault();
        onPrev?.();
      }
      if (e.key === "ArrowRight" && hasNext) {
        e.preventDefault();
        onNext?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasNext, hasPrev, isOpen, onClose, onNext, onPrev]);

  useEffect(() => {
    if (!isOpen || !done || !storageKey || typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(storageKey, "1");
    } catch {
      // ignore storage errors
    }
  }, [done, isOpen, storageKey]);

  useEffect(() => {
    if (!showInlineContact || !scrollAreaRef.current) return;
    scrollAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }, [showInlineContact]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[110]">
      <button
        type="button"
        aria-label="Detailansicht schliessen"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="relative h-full w-full flex items-center justify-center p-4">
        <div className="w-full max-w-3xl rounded-lg border border-[#AC8E66]/30 overflow-hidden shadow-2xl shadow-black/70 bg-[#0F0F0F] font-mono">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
            <button
              type="button"
              onClick={onClose}
              className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 transition"
              aria-label="Schliessen"
              title="Schliessen"
            />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-xs text-[#AC8E66]">{title}</span>
          </div>

          <div ref={scrollAreaRef} className="px-4 sm:px-6 py-5 max-h-[70vh] overflow-y-auto">
            <p className="text-xs text-[#EFEBDC]/50 mb-3">
              <span className="text-[#AC8E66]">$</span> {subtitle}
            </p>

            {showInlineContact ? (
              <div className="mb-5 pb-4 border-b border-[#AC8E66]/15">
                <ContactForm embedded onClose={() => setShowInlineContact(false)} />
              </div>
            ) : null}

            {visibleLines.map((line, index) => (
              <p
                key={`${line.type}-${index}`}
                className={[
                  "text-sm leading-relaxed mt-1 flex items-start gap-2",
                  line.type === "prompt" ? "text-[#AC8E66]" : "",
                  line.type === "response" ? "text-[#EFEBDC]" : "",
                  line.type === "status" ? "text-green-400/90" : "",
                  line.type === "error" ? "text-[#ff6b6b]" : "",
                ].join(" ")}
              >
                <span className="text-[#AC8E66]/60 shrink-0">
                  {line.type === "prompt" ? "$" : ">"}
                </span>
                <span className="min-w-0 flex-1">
                  {line.displayText}
                  {!done && index === visibleLines.length - 1 ? (
                    <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-[2px] align-middle animate-cursor-blink" />
                  ) : null}
                </span>
              </p>
            ))}

          </div>

          <div className="flex items-center gap-3 px-4 sm:px-6 py-2.5 bg-[#1a1a1a] border-t border-[#AC8E66]/10">
            <button
              type="button"
              onClick={onPrev}
              disabled={!hasPrev}
              className={[
                "inline-flex items-center gap-1.5 text-xs transition-colors",
                hasPrev
                  ? "text-[#EFEBDC]/80 hover:text-[#EFEBDC]"
                  : "text-[#EFEBDC]/30 cursor-not-allowed",
              ].join(" ")}
            >
              <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                prev
              </kbd>
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!hasNext}
              className={[
                "inline-flex items-center gap-1.5 text-xs transition-colors",
                hasNext
                  ? "text-[#EFEBDC]/80 hover:text-[#EFEBDC]"
                  : "text-[#EFEBDC]/30 cursor-not-allowed",
              ].join(" ")}
            >
              <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                next
              </kbd>
            </button>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#EFEBDC]/55">
              <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                ←
              </kbd>
              <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                →
              </kbd>
            </span>
            {ctaLabel && (ctaPath || ctaMode === "inlineContact") ? (
              <button
                type="button"
                onClick={() => {
                  if (ctaMode === "inlineContact") {
                    setShowInlineContact(true);
                    return;
                  }
                  onClose?.();
                  navigate(ctaPath);
                }}
                className="inline-flex items-center gap-1.5 text-xs text-[#EFEBDC]/80 hover:text-[#EFEBDC] transition-colors"
              >
                <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                  {ctaLabel}
                </kbd>
                kontakt
              </button>
            ) : null}
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#EFEBDC]/55">
              <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                esc
              </kbd>
              schliessen
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#EFEBDC]/55">
              <kbd className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                click
              </kbd>
              außerhalb
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-[#EFEBDC]/55">
              <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-[10px] leading-none">
                ●
              </kbd>
              <span className="sm:hidden">tippe rot zum schliessen</span>
              <span className="hidden sm:inline">rot</span>
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
